import React, { useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { ChevronDownIcon, ChevronUpIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { Alert, Button } from '../common/UIComponents';
import { RepairRequest } from '../../types/firestore';
import { REPAIR_REQUEST_OPERATIONAL_INDEX_FALLBACK_LABEL } from '../../shared/repairRequestDisplay';
import {
  buildRepairRequestPrintHubModel,
  isOperationalPrintVariant,
  type RepairRequestPrintVariantId,
} from '../../shared/repairRequestPrint';

interface RepairRequestPrintHubProps {
  request: RepairRequest;
}

const normalizeLabel = (value: unknown, fallback: string): string => {
  if (typeof value !== 'string') {
    return fallback;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : fallback;
};

const PrintField: React.FC<{
  label: string;
  value: React.ReactNode;
  className?: string;
}> = ({ label, value, className }) => (
  <div className={`rounded-lg border border-slate-200 bg-slate-50 p-3 ${className || ''}`}>
    <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
    <div className="mt-1 text-sm leading-snug text-slate-900" style={{ wordBreak: 'break-word' }}>
      {value}
    </div>
  </div>
);

const PrintSheetFrame: React.FC<{
  variantId: RepairRequestPrintVariantId;
  title: string;
  subtitle: string;
  headerLabel: string;
  headerValue: string;
  children: React.ReactNode;
  footerNote: string;
  className?: string;
}> = ({
  variantId,
  title,
  subtitle,
  headerLabel,
  headerValue,
  children,
  footerNote,
  className,
}) => (
  <section
    data-testid={`request-print-sheet-${variantId}`}
    className={`print-only request-print-sheet mx-auto w-full rounded-2xl border border-slate-300 bg-white p-6 text-slate-900 shadow-none ${className || ''}`}
  >
    <header className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          {headerLabel}
        </p>
        <h3 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">{title}</h3>
        <p className="mt-1 text-sm leading-snug text-slate-600">{subtitle}</p>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-xs uppercase tracking-wide text-slate-500">Indeks operacyjny</p>
        <p className="mt-1 break-all font-mono text-lg font-semibold tracking-[0.18em] text-slate-900">
          {headerValue}
        </p>
      </div>
    </header>

    <div className="mt-4 space-y-4">{children}</div>

    <footer className="mt-4 border-t border-slate-200 pt-3 text-xs leading-relaxed text-slate-500">
      {footerNote}
    </footer>
  </section>
);

const RenderIdentitySheet: React.FC<{
  model: ReturnType<typeof buildRepairRequestPrintHubModel>;
}> = ({ model }) => (
  <PrintSheetFrame
    variantId="identity"
    title="Identyfikator sprawy"
    subtitle="Wydruk do akt, segregatora i szybkiej identyfikacji sprawy."
    headerLabel="Druk serwisowy"
    headerValue={model.operationalIndex.value || REPAIR_REQUEST_OPERATIONAL_INDEX_FALLBACK_LABEL}
    className="max-w-4xl"
    footerNote="Źródło danych: zapis sprawy i indeks operacyjny. Druk nie tworzy nowej numeracji."
  >
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <PrintField label="ID zlecenia" value={model.requestId || 'Brak ID'} />
      <PrintField label="Kod intake" value={model.requestIndex || 'Brak kodu intake'} />
      <PrintField label="Klient" value={model.clientLabel} />
      <PrintField label="Serwis" value={model.serviceReferenceLabel} />
      <PrintField label="Typ sprzętu" value={model.deviceLabel} />
      <PrintField label="Marka i model" value={model.brandModelLabel || 'Brak marki i modelu'} />
      <PrintField label="Status" value={model.statusLabel} />
      <PrintField label="Etap" value={model.stageLabel} />
      <PrintField label="Utworzono" value={model.createdAtLabel} />
      <PrintField label="Zaktualizowano" value={model.updatedAtLabel} />
      <PrintField
        label="Indeks nadano"
        value={model.operationalIndexAssignedAtLabel || 'Brak potwierdzonego czasu nadania'}
      />
      <PrintField label="Następny krok" value={model.nextStep} />
    </div>

    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <PrintField
        label="Skrócony opis"
        value={<p className="whitespace-pre-wrap">{model.issueSummary}</p>}
      />
      <PrintField
        label="Pełny opis"
        value={<p className="whitespace-pre-wrap">{model.issueDescription}</p>}
      />
    </div>
  </PrintSheetFrame>
);

const RenderReceiptSheet: React.FC<{
  model: ReturnType<typeof buildRepairRequestPrintHubModel>;
}> = ({ model }) => (
  <PrintSheetFrame
    variantId="receipt"
    title="Potwierdzenie przyjęcia"
    subtitle="Wydruk potwierdza przyjęcie sprawy do obsługi serwisowej."
    headerLabel="Dokument przyjęcia"
    headerValue={normalizeLabel(
      model.operationalIndex.value,
      REPAIR_REQUEST_OPERATIONAL_INDEX_FALLBACK_LABEL
    )}
    className="max-w-4xl"
    footerNote="Potwierdzenie korzysta z zapisanych danych sprawy i tego samego indeksu operacyjnego."
  >
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <PrintField label="ID zlecenia" value={model.requestId || 'Brak ID'} />
      <PrintField label="Kod intake" value={model.requestIndex || 'Brak kodu intake'} />
      <PrintField label="Klient" value={model.clientLabel} />
      <PrintField label="Kontakt" value={model.clientEmail || 'Brak adresu e-mail'} />
      <PrintField label="Serwis" value={model.serviceReferenceLabel} />
      <PrintField label="Status sprawy" value={model.statusLabel} />
      <PrintField label="Typ sprzętu" value={model.deviceLabel} />
      <PrintField label="Marka i model" value={model.brandModelLabel || 'Brak marki i modelu'} />
      <PrintField label="Utworzono" value={model.createdAtLabel} />
      <PrintField label="Zaktualizowano" value={model.updatedAtLabel} />
      <PrintField
        label="Indeks nadano"
        value={model.operationalIndexAssignedAtLabel || 'Brak potwierdzonego czasu nadania'}
      />
      <PrintField label="Etap" value={model.stageLabel} />
    </div>

    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <PrintField
        label="Opis przyjęcia"
        value={<p className="whitespace-pre-wrap">{model.issueSummary}</p>}
      />
      <PrintField
        label="Uwagi robocze"
        value={<p className="whitespace-pre-wrap">{model.issueDescription}</p>}
      />
    </div>

    <div className="grid grid-cols-1 gap-4 pt-2 sm:grid-cols-2">
      <div className="rounded-lg border border-slate-300 bg-white p-4">
        <p className="text-[10px] uppercase tracking-wide text-slate-500">Podpis serwisu</p>
        <div className="mt-6 border-t border-slate-400 pt-2 text-xs text-slate-500">
          Przyjął zgłoszenie
        </div>
      </div>
      <div className="rounded-lg border border-slate-300 bg-white p-4">
        <p className="text-[10px] uppercase tracking-wide text-slate-500">Podpis klienta</p>
        <div className="mt-6 border-t border-slate-400 pt-2 text-xs text-slate-500">
          Potwierdzenie przekazania
        </div>
      </div>
    </div>
  </PrintSheetFrame>
);

const RenderLabelSheet: React.FC<{
  model: ReturnType<typeof buildRepairRequestPrintHubModel>;
}> = ({ model }) => (
  <PrintSheetFrame
    variantId="label"
    title="Etykieta / naklejka"
    subtitle="Kompaktowy wydruk do fizycznego oznaczenia sprzętu lub opakowania."
    headerLabel="Naklejka serwisowa"
    headerValue={normalizeLabel(
      model.operationalIndex.value,
      REPAIR_REQUEST_OPERATIONAL_INDEX_FALLBACK_LABEL
    )}
    className="max-w-[92mm]"
    footerNote="Etykieta korzysta z tego samego indeksu operacyjnego i nie generuje kodu zastępczego."
  >
    <div className="space-y-3 rounded-2xl border-2 border-slate-300 bg-white p-4 text-center">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        Sprawa serwisowa
      </p>
      <p className="break-all font-mono text-3xl font-semibold tracking-[0.2em] text-slate-900">
        {normalizeLabel(model.operationalIndex.value, 'Brak indeksu')}
      </p>
      <p className="text-sm font-semibold text-slate-900">{model.deviceLabel}</p>
      <p className="text-sm text-slate-700">{model.brandModelLabel || 'Brak marki i modelu'}</p>
      <p className="text-xs leading-relaxed text-slate-600">{model.issueSummary}</p>
    </div>

    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <PrintField label="ID zlecenia" value={model.requestId || 'Brak ID'} />
      <PrintField label="Kod intake" value={model.requestIndex || 'Brak kodu intake'} />
      <PrintField label="Klient" value={model.clientLabel} />
      <PrintField label="Serwis" value={model.serviceReferenceLabel} />
    </div>
  </PrintSheetFrame>
);

const RenderPlannedSealSheet: React.FC = () => (
  <PrintSheetFrame
    variantId="seal"
    title="Plomba"
    subtitle="Wariant plomby operacyjnej jest jeszcze przygotowywany."
    headerLabel="Wariant przyszły"
    headerValue="W przygotowaniu"
    className="max-w-md"
    footerNote="Ten wariant nie jest jeszcze aktywny i nie jest dostępny do drukowania."
  >
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-700">
      Wariant plomby pozostaje wyłącznie zapowiedzią kolejnego etapu. Nie generuje numeracji, nie
      tworzy kodu zastępczego i nie zastępuje indeksu operacyjnego.
    </div>
  </PrintSheetFrame>
);

const RepairRequestPrintHub: React.FC<RepairRequestPrintHubProps> = ({ request }) => {
  const model = useMemo(() => buildRepairRequestPrintHubModel(request), [request]);
  const [isVariantsOpen, setIsVariantsOpen] = useState(false);
  const [isTechnicalOpen, setIsTechnicalOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<RepairRequestPrintVariantId>('identity');
  const [printError, setPrintError] = useState<string | null>(null);

  useEffect(() => {
    setIsVariantsOpen(false);
    setIsTechnicalOpen(false);
    setSelectedVariant('identity');
    setPrintError(null);
  }, [model.requestId]);

  const printVariant = (variantId: RepairRequestPrintVariantId) => {
    if (variantId === 'seal') {
      setPrintError('Wariant plomby jest jeszcze w przygotowaniu.');
      setIsVariantsOpen(true);
      return;
    }

    if (isOperationalPrintVariant(variantId) && !model.operationalIndex.isAssigned) {
      setPrintError('Warianty operacyjne są dostępne dopiero po nadaniu indeksu operacyjnego.');
      setIsVariantsOpen(true);
      return;
    }

    if (typeof window === 'undefined' || typeof window.print !== 'function') {
      setPrintError('Drukowanie nie jest dostępne w tej przeglądarce.');
      return;
    }

    flushSync(() => {
      setSelectedVariant(variantId);
      setPrintError(null);
    });
    window.print();
  };

  return (
    <section
      data-testid="service-detail-identification"
      className="request-print-root space-y-3 rounded-lg bg-primary-dark-100/10 p-3 ring-1 ring-primary-dark-300/18 sm:p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary-light-400">
            Wydruki i identyfikacja
          </p>
          <p className="text-xs leading-snug text-primary-light-300">
            Wydruki korzystają z indeksu operacyjnego tej sprawy.
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="neutral"
            size="sm"
            icon={<PrinterIcon className="h-4 w-4" />}
            onClick={() => printVariant('identity')}
          >
            Drukuj identyfikator
          </Button>
          <Button
            variant="neutral"
            size="sm"
            icon={
              isVariantsOpen ? (
                <ChevronUpIcon className="h-4 w-4" />
              ) : (
                <ChevronDownIcon className="h-4 w-4" />
              )
            }
            onClick={() => setIsVariantsOpen((current) => !current)}
            aria-expanded={isVariantsOpen}
            aria-controls="repair-request-print-variants"
          >
            {isVariantsOpen ? 'Ukryj warianty' : 'Warianty druków'}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary-dark-300/24 bg-primary-dark-100/12 p-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary-light-400">
            Indeks operacyjny
          </p>
          {model.operationalIndex.isAssigned ? (
            <p className="mt-1 break-all font-mono text-lg font-semibold tracking-normal text-primary-light-100 sm:text-xl">
              {model.operationalIndex.value}
            </p>
          ) : (
            <p className="mt-1 text-sm leading-snug text-primary-light-300">
              Indeks zostanie nadany po rozpoczęciu realizacji.
            </p>
          )}
        </div>
        {!model.operationalIndex.isAssigned && (
          <span className="rounded-full border border-primary-dark-300/50 bg-primary-dark-200/18 px-2.5 py-1 text-[11px] text-primary-light-300">
            Druki operacyjne zablokowane
          </span>
        )}
      </div>

      {printError && <Alert variant="error">{printError}</Alert>}

      {isVariantsOpen && (
        <div
          id="repair-request-print-variants"
          className="overflow-hidden rounded-lg border border-primary-dark-300/24 bg-primary-dark-100/10"
        >
          {model.variants.map((variant) => {
            const isSelected = selectedVariant === variant.id;
            const isLocked = variant.availability !== 'ready';
            const isPlanned = variant.availability === 'planned';

            return (
              <article
                key={variant.id}
                className="grid grid-cols-1 gap-2 border-b border-primary-dark-300/20 p-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-primary-light-100">{variant.title}</p>
                    <p className="mt-0.5 text-xs leading-snug text-primary-light-300">
                      {variant.description}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${
                      isPlanned
                        ? 'border-primary-dark-300/70 bg-primary-dark-200/40 text-primary-light-300'
                        : isLocked
                          ? 'border-status-warning-500/35 bg-status-warning-500/10 text-status-warning-500'
                          : isSelected
                            ? 'border-ratuj-300/30 bg-ratuj-300/10 text-ratuj-200'
                            : 'border-primary-dark-300/70 bg-primary-dark-200/35 text-primary-light-300'
                    }`}
                  >
                    {isPlanned ? 'Plomba w przygotowaniu' : isLocked ? 'Zablokowany' : 'Gotowy'}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                  <p className="min-w-0 flex-1 text-[11px] text-primary-light-400 sm:max-w-[16rem] sm:text-right">
                    {variant.requiresOperationalIndex
                      ? model.operationalIndex.isAssigned
                        ? 'Wariant korzysta z tego samego indeksu operacyjnego.'
                        : 'Oczekuje na nadanie indeksu operacyjnego.'
                      : 'Dostępny także bez indeksu operacyjnego.'}
                  </p>
                  <Button
                    variant={isSelected ? 'primary' : 'neutral'}
                    size="sm"
                    icon={
                      isPlanned ? (
                        <ChevronDownIcon className="h-4 w-4" />
                      ) : (
                        <PrinterIcon className="h-4 w-4" />
                      )
                    }
                    onClick={() => printVariant(variant.id)}
                    disabled={isLocked}
                  >
                    {isPlanned ? 'W przygotowaniu' : isSelected ? 'Wybrany' : 'Drukuj'}
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <div className="border-t border-primary-dark-300/20 pt-2">
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs font-medium text-primary-light-400 transition hover:text-primary-light-200 focus-outline"
          onClick={() => setIsTechnicalOpen((current) => !current)}
          aria-expanded={isTechnicalOpen}
          aria-controls="repair-request-technical-identification"
        >
          {isTechnicalOpen ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          )}
          Dane techniczne
        </button>

        {isTechnicalOpen && (
          <div
            id="repair-request-technical-identification"
            className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2"
          >
            <PrintField label="ID zlecenia" value={model.requestId || 'Brak ID'} />
            <PrintField label="Kod intake" value={model.requestIndex || 'Brak kodu intake'} />
          </div>
        )}
      </div>

      <div className="print-only">
        {selectedVariant === 'identity' && <RenderIdentitySheet model={model} />}
        {selectedVariant === 'receipt' && <RenderReceiptSheet model={model} />}
        {selectedVariant === 'label' && <RenderLabelSheet model={model} />}
        {selectedVariant === 'seal' && <RenderPlannedSealSheet />}
      </div>
    </section>
  );
};

export default RepairRequestPrintHub;
